import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import type { SelectionProps, StatisticCardSelectionValue } from '../types'

export interface UseSelectionReturn {
  /** 归一化后的 controlType（无选择模式时为 null）。 */
  controlType: ComputedRef<'radio' | 'checkbox' | null>
  /** 判断某个 id 当前是否被选中。 */
  isSelected: (target: StatisticCardSelectionValue) => boolean
  /**
   * 判断某个 id 是否因达到 maxLength / minLength 上下限
   * 而不能再切换（仅在 checkbox 模式下生效）。
   */
  isSelectionDisabled: (target: StatisticCardSelectionValue) => boolean
  /**
   * 切换某个 id 的选中态。
   * - radio：直接把 value 切到 target
   * - checkbox：在数组内添加/移除 target，并遵守 minLength/maxLength
   * - 内部同时触发 `onChange` 与 `'onUpdate:value'`，以兼容 React 用法与 v-model:value
   */
  toggle: (target: StatisticCardSelectionValue) => void
}

/**
 * 选择模式核心逻辑（对应 React 端 useSelection）。
 *
 * 用法：容器组件接收 SelectionProps，
 * 调用 `useSelection(props)` 拿到操作函数，再通过 provide 下发到卡片：
 *
 * ```ts
 * const selection = useSelection(props)
 * provide(statisticCardSelectionKey, {
 *   controlType: selection.controlType,
 *   isChecked: selection.isSelected,
 *   isLocked: selection.isSelectionDisabled,
 *   toggle: selection.toggle,
 * })
 * ```
 */
export function useSelection(props: SelectionProps): UseSelectionReturn {
  const controlType = computed(() => {
    if (props.controlType === 'radio' || props.controlType === 'checkbox') {
      return props.controlType
    }
    return null
  })

  const selectedValues = computed<StatisticCardSelectionValue[]>(() => {
    if (props.controlType === 'checkbox') {
      return props.value ?? []
    }
    return []
  })

  const selectedSet = computed(() => new Set<StatisticCardSelectionValue>(selectedValues.value))

  const isSelected = (target: StatisticCardSelectionValue) => {
    if (props.controlType === 'radio') {
      return props.value === target
    }
    if (props.controlType === 'checkbox') {
      return selectedSet.value.has(target)
    }
    return false
  }

  const isSelectionDisabled = (target: StatisticCardSelectionValue) => {
    if (props.controlType !== 'checkbox') {
      return false
    }
    const checked = selectedSet.value.has(target)
    const values = selectedValues.value

    if (checked && props.minLength !== undefined && values.length <= props.minLength) {
      return true
    }
    if (!checked && props.maxLength !== undefined && values.length >= props.maxLength) {
      return true
    }
    return false
  }

  /**
   * 内部辅助：同时触发两个事件出口，
   * 保证 React 风格 onChange 与 Vue v-model:value 两条链路一致。
   */
  const emitChange = (
    next: StatisticCardSelectionValue | StatisticCardSelectionValue[],
  ) => {
    const onChange = (props as { onChange?: (v: any) => void }).onChange
    const onUpdate = (props as { 'onUpdate:value'?: (v: any) => void })['onUpdate:value']
    onChange?.(next)
    onUpdate?.(next)
  }

  const toggle = (target: StatisticCardSelectionValue) => {
    if (props.controlType === 'radio') {
      emitChange(target)
      return
    }
    if (props.controlType !== 'checkbox') {
      return
    }

    const checked = selectedSet.value.has(target)
    const values = selectedValues.value

    if (!checked && props.maxLength !== undefined && values.length >= props.maxLength) {
      return
    }
    if (checked && props.minLength !== undefined && values.length <= props.minLength) {
      return
    }

    const next = checked ? values.filter((item) => item !== target) : [...values, target]
    emitChange(next)
  }

  return {
    controlType,
    isSelected,
    isSelectionDisabled,
    toggle,
  }
}